import { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Select,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { IconCircleCheck, IconSend } from '@tabler/icons-react'
import { DatePickerInput } from '@mantine/dates'
import {
  buildOutlookComposeUrl,
  formatDate,
  INITIAL_VALUES,
  REQUEST_RECIPIENT,
  type IntakeValues,
} from './intake'

// ---------------------------------------------------------------------------
// Data Engineering intake form. Fields mirror the "Data Engineering Intake
// Form" workbook: 7 sections, (Required) fields must be completed. On submit
// it opens an Outlook (web) compose window prefilled with a
// "Prism Portal Request" email to enterprisedata@loandepot.com.
//
// NOTE (interim): the email hand-off is a stopgap. In a later release this
// will transition to a more robust Linear integration.
// ---------------------------------------------------------------------------

const required = (message: string) => (v: string) =>
  String(v ?? '').trim().length === 0 ? message : null

export function DataEngineeringIntakeForm() {
  const [submitted, setSubmitted] = useState(false)

  const form = useForm<IntakeValues>({
    initialValues: INITIAL_VALUES,
    validate: {
      subject: required('Subject is required'),
      requestor: required('Requestor is required'),
      project: required('Project / Initiative is required'),
      businessOwner: required('Business Owner is required'),
      priority: required('Priority is required'),
      problemStatement: required('Problem Statement is required'),
      businessImpact: required('Business Impact is required'),
      sourceSystem: required('Source System is required'),
      dataFormat: required('Data Format is required'),
      frequency: required('Frequency is required'),
      pii: required('Please indicate whether the data contains PII'),
    },
  })

  const handleSubmit = form.onSubmit((values) => {
    const loginHint = localStorage.getItem('prism_username')
    window.open(
      buildOutlookComposeUrl(values, loginHint),
      '_blank',
      'noopener,noreferrer'
    )
    setSubmitted(true)
  })

  const SectionHeader = ({
    step,
    title,
  }: {
    step: number
    title: string
  }) => (
    <Text size="lg" fw={600} c="gray.9" mt="lg" mb="sm">
      {step}. {title}
    </Text>
  )

  return (
    <Box>
      <Text size="sm" c="gray.6" mb="md">
        Submit a Data Engineering intake request covering the full medallion
        pipeline. Fields marked (Required) must be completed. Submitting opens
        an Outlook email to {REQUEST_RECIPIENT}.
      </Text>

      {submitted && (
        <Alert
          color="green"
          icon={<IconCircleCheck size={16} />}
          title="Email drafted in Outlook"
          mb="md"
          withCloseButton
          onClose={() => setSubmitted(false)}
        >
          A &quot;Prism Portal Request&quot; email to {REQUEST_RECIPIENT} should
          have opened in an Outlook compose tab. Review and send it to complete
          your request. If nothing opened, allow pop-ups for this site and try
          again.
        </Alert>
      )}

      <Card shadow="sm" withBorder>
        <Box p="md">
          <form onSubmit={handleSubmit}>
            {/* Subject */}
            <TextInput
              label="Subject (Required)"
              description="Briefly describe what this request is about"
              placeholder="e.g. Sales Reporting Pipeline Ingestion"
              mb="lg"
              styles={{ label: { fontSize: 'var(--mantine-font-size-lg)', fontWeight: 600 } }}
              {...form.getInputProps('subject')}
            />

            {/* 1. Request Info */}
            <SectionHeader step={1} title="Request Info" />
            <Group grow mb="md">
              <TextInput
                label="Requestor (Required)"
                placeholder="e.g. Jane Smith / Analytics Team"
                {...form.getInputProps('requestor')}
              />
              <TextInput
                label="Project / Initiative (Required)"
                placeholder="e.g. Sales Reporting Pipeline"
                {...form.getInputProps('project')}
              />
            </Group>
            <Group grow mb="md">
              <TextInput
                label="Business Owner (Required)"
                placeholder="Stakeholder responsible"
                {...form.getInputProps('businessOwner')}
              />
              <Select
                label="Priority (Required)"
                data={[
                  { value: 'high', label: 'High' },
                  { value: 'medium', label: 'Medium' },
                  { value: 'low', label: 'Low' },
                ]}
                {...form.getInputProps('priority')}
              />
              <DatePickerInput
                label="Requested Go-Live"
                placeholder="Pick a date"
                clearable
                valueFormat="M/D/YYYY"
                value={form.values.goLive ? new Date(form.values.goLive) : new Date()}
                onChange={(d) => form.setFieldValue('goLive', d ? formatDate(d) : '')}
                error={form.errors.goLive}
              />
            </Group>

            {/* 2. Business Context */}
            <SectionHeader step={2} title="Business Context" />
            <Textarea
              label="Problem Statement (Required)"
              placeholder="What problem are we solving?"
              minRows={2}
              autosize
              mb="md"
              {...form.getInputProps('problemStatement')}
            />
            <Textarea
              label="Business Impact (Required)"
              placeholder="Why is this important? What happens if not done?"
              minRows={2}
              autosize
              mb="md"
              {...form.getInputProps('businessImpact')}
            />
            <Textarea
              label="Use Case / Scenario"
              placeholder="Describe the scenario where this data will be used"
              minRows={2}
              autosize
              mb="md"
              {...form.getInputProps('useCase')}
            />

            {/* 3. Source Details */}
            <SectionHeader step={3} title="Source Details" />
            <Group grow mb="md">
              <Select
                label="Source System (Required)"
                placeholder="Select"
                data={['Database', 'API', 'File', 'Streaming']}
                {...form.getInputProps('sourceSystem')}
              />
              <Select
                label="Data Format (Required)"
                placeholder="Select"
                data={['CSV', 'JSON', 'Parquet', 'API']}
                {...form.getInputProps('dataFormat')}
              />
              <Select
                label="Frequency (Required)"
                placeholder="Select"
                data={['Daily', 'Hourly', 'Streaming']}
                {...form.getInputProps('frequency')}
              />
            </Group>
            <Group grow mb="md">
              <TextInput
                label="Source Location"
                placeholder="URL, path, database, or topic"
                {...form.getInputProps('sourceLocation')}
              />
              <TextInput
                label="Source Contact"
                placeholder="Who owns the source"
                {...form.getInputProps('sourceContact')}
              />
            </Group>

            {/* 4. Landing / Bronze */}
            <SectionHeader step={4} title="Landing / Bronze" />
            <Group grow mb="md">
              <TextInput
                label="Landing Path"
                placeholder="Cloud storage (S3 / ADLS / GCS)"
                {...form.getInputProps('landingPath')}
              />
              <TextInput
                label="Bronze Table Name"
                placeholder="Databricks Delta table name"
                {...form.getInputProps('bronzeTable')}
              />
            </Group>
            <Group grow mb="md">
              <TextInput
                label="Retention"
                placeholder="How long to keep raw data"
                {...form.getInputProps('retention')}
              />
              <Select
                label="Schema Enforcement"
                placeholder="Select"
                clearable
                data={['Yes', 'No']}
                {...form.getInputProps('schemaEnforcement')}
              />
            </Group>
            <Textarea
              label="Notes"
              placeholder="Any special requirements"
              minRows={2}
              autosize
              mb="md"
              {...form.getInputProps('bronzeNotes')}
            />

            {/* 5. Transformations / Silver */}
            <SectionHeader step={5} title="Transformations / Silver" />
            <Group grow mb="md">
              <Textarea
                label="Cleansing"
                placeholder="Null handling, type checks"
                minRows={2}
                autosize
                {...form.getInputProps('cleansing')}
              />
              <Textarea
                label="Business Rules"
                placeholder="Any logic to apply"
                minRows={2}
                autosize
                {...form.getInputProps('businessRules')}
              />
            </Group>
            <Group grow mb="md">
              <Select
                label="Deduplication"
                placeholder="Select"
                clearable
                data={['Yes', 'No']}
                {...form.getInputProps('deduplication')}
              />
              <TextInput
                label="Target Table"
                placeholder="Silver layer table name"
                {...form.getInputProps('targetTable')}
              />
            </Group>

            {/* 6. Gold / Consumption */}
            <SectionHeader step={6} title="Gold / Consumption" />
            <Group grow mb="md">
              <TextInput
                label="Output Table"
                placeholder="Gold layer table name"
                {...form.getInputProps('outputTable')}
              />
              <TextInput
                label="Consumers"
                placeholder="BI dashboards / ML / Reports"
                {...form.getInputProps('consumers')}
              />
            </Group>
            <Textarea
              label="Aggregations"
              placeholder="Daily totals, KPIs, etc."
              minRows={2}
              autosize
              mb="md"
              {...form.getInputProps('aggregations')}
            />

            {/* 7. Data Quality & Governance */}
            <SectionHeader step={7} title="Data Quality & Governance" />
            <Group grow mb="md">
              <Select
                label="PII / Sensitive Data (Required)"
                placeholder="Select"
                data={['Yes', 'No']}
                {...form.getInputProps('pii')}
              />
              <TextInput
                label="Access"
                placeholder="Who can read / write"
                {...form.getInputProps('access')}
              />
            </Group>
            <Textarea
              label="Validation Rules"
              placeholder="Null check, type check, thresholds"
              minRows={2}
              autosize
              mb="md"
              {...form.getInputProps('validationRules')}
            />

            <Divider my="md" />
            <Group justify="flex-end">
              <Button
                variant="light"
                color="gray"
                onClick={() => {
                  form.reset()
                  setSubmitted(false)
                }}
              >
                Clear
              </Button>
              <Button
                type="submit"
                color="prism"
                leftSection={<IconSend size={16} />}
              >
                Submit Request
              </Button>
            </Group>
          </form>
        </Box>
      </Card>
    </Box>
  )
}
